/**
 * Surface content — 2.4 Template detail (`/library/templates/:templateId`).
 *
 * ZERO literals in the component (build brief §0a): every figure, name, id,
 * route and sentence the surface renders is declared here or read from
 * `@/demo/data`.
 *
 * ONE PACK IS AUTHORED. The library lists many; this prototype holds the
 * retail rollout pack in full, and `packById` resolves ANY id to it (see the
 * note on that function) so a stray link from the library, the register or
 * the test surface never dead-ends on an empty detail page.
 *
 * MIRROR CONTRACT. The header figures below — pack name, industry × expertise,
 * maintainer, `Reused 9×`, `Saves ~11 days`, and the `V4 · ODOO 19` context
 * pill — are the SAME figures `content/templateLibrary.ts` carries for this
 * pack's row in the library table. They are stated once per surface because
 * the two modules are authored in the same wave; if either moves, both move.
 *
 * CROSS-SURFACE FACTS (already on other surfaces, restated here as this pack's
 * own history, never re-derived):
 *  - The 22 regression cases are the deck's own line — "the seven pricing
 *    failures produced 22 new regression cases attached to retail pricing pack
 *    v4" — and `content/testUat.ts` (`LIBRARY_FEEDBACK`) states it from the
 *    project side. This surface is the receiving end of that same event.
 *  - The Bergman promotion rule is the deck's interview card, "similar rule
 *    solved for Bergman Winkels — template v4 offered", seen from the library.
 *
 * HONESTY. Bergman Winkels is on Odoo 18 in the seed, so it is NOT claimed to
 * be running v4 of an Odoo 19 pack: its row records the version it installed
 * (v3) and its rule is shown arriving INTO v4. Van Dijk is in Build, so its
 * regression cases are the nightly runs against the build, not UAT results.
 */

import { NOW, OFFICES, PHASES, personById, projectById } from '@/demo/data'
import type { OfficeCode, PhaseKey } from '@/demo/data'

/* ── A sentence that carries links, without literals in the component ───── */

export interface Fragment {
  text: string
  /** Present when this fragment is a link to a real route. */
  to?: string
}

/* ── Dates (measured against NOW — never Date.now()) ────────────────────── */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

const dayLabel = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`)
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()] ?? ''} ${d.getUTCFullYear()}`
}

const daysAgo = (iso: string, from: Date): number =>
  Math.floor((from.getTime() - new Date(`${iso}T00:00:00Z`).getTime()) / 86_400_000)

const phaseName = (key: PhaseKey): string => PHASES.find((p) => p.key === key)?.name ?? ''

/** Same shape the portfolio board uses, so an office reads identically here. */
const officeLabel = (code: OfficeCode): string => {
  const office = OFFICES.find((o) => o.code === code)
  if (!office) throw new Error(`Unknown office: ${code}`)
  return `${office.code} · ${office.city}`
}

/* ── Real routes this surface links to (no dead links, no href="#") ─────── */

const SOURCE_PROJECT_ID = 'van-dijk'
const PROMO_SOURCE_ID = 'bergman'
const ROUNDING_SOURCE_ID = 'lumiere'

export const HREFS = {
  templates: '/library/templates',
  vanDijk: `/project/${SOURCE_PROJECT_ID}`,
  vanDijkTest: `/project/${SOURCE_PROJECT_ID}/test`,
  vanDijkBlueprint: `/project/${SOURCE_PROJECT_ID}/blueprint`,
  projects: '/projects',
  blueprints: '/library/blueprints',
  training: '/resources/training',
} as const

export const ANCHORS = {
  contents: 'contents',
  usedBy: 'used-by',
  improvements: 'improvements-returned',
  actions: 'actions',
  compare: 'compare-v3-v4',
} as const

/* ── The pack ───────────────────────────────────────────────────────────── */

const MAINTAINER = personById('marc-dubois')

export interface TemplatePack {
  id: string
  name: string
  industry: string
  expertise: string
  version: string
  previousVersion: string
  odooVersion: number
  reuseCount: number
  timeSavedDays: number
  updated: string
  maintainerId: string
}

/**
 * The one authored pack. `version`/`odooVersion` are what the shell's context
 * pill says, spelled once here so the pill and the body cannot disagree.
 */
export const PACK: TemplatePack = {
  id: 'retail-rollout',
  name: 'Retail rollout pack',
  industry: 'Retail',
  expertise: 'Pricing/Promotions',
  version: 'v4',
  previousVersion: 'v3',
  odooVersion: 19,
  reuseCount: 9,
  timeSavedDays: 11,
  updated: '2026-08-21',
  maintainerId: MAINTAINER.id,
}

/**
 * Resolves the route param. Only `PACK.id` is authored, so every other id —
 * and a missing one — lands on this pack rather than a dead page; the density
 * note says as much on screen.
 */
export const packById = (_templateId: string | undefined): TemplatePack => PACK

export const MAINTAINER_LINK = {
  name: MAINTAINER.name,
  meta: `${MAINTAINER.role} · ${officeLabel(MAINTAINER.office)}`,
  href: `/resources/people/${MAINTAINER.id}`,
} as const

/* ── Chrome ─────────────────────────────────────────────────────────────── */

export const TEMPLATE_DETAIL_PAGE = {
  moduleLabel: 'PX · Library',
  contextPill: `${PACK.version.toUpperCase()} · ODOO ${PACK.odooVersion}`,
  breadcrumbRoot: 'Templates',
  breadcrumbSeparator: ' › ',
  standfirst:
    'What the pack contains, which projects are running it, and what those projects sent back. Opening it ' +
    'inside a project pre-fills that project’s blueprint.',
} as const

export const HEADER = {
  title: PACK.name,
  /** industry × expertise · maintainer · updated — the maintainer is a link. */
  lineBefore: `${PACK.industry} × ${PACK.expertise} · maintained by `,
  lineAfter:
    ` · updated ${dayLabel(PACK.updated)} (${daysAgo(PACK.updated, NOW)} days ago)`,
  reusePill: `Reused ${PACK.reuseCount}×`,
  savesPill: `Saves ~${PACK.timeSavedDays} days`,
  backLabel: 'All templates',
} as const

/* ── 1 · Contents (asset · type · phase) ────────────────────────────────── */

export type AssetType = 'Blueprint' | 'Config' | 'Mapping' | 'Test set' | 'Training'

export interface TemplateAsset {
  key: string
  asset: string
  type: AssetType
  phase: PhaseKey
  phaseLabel: string
  /** Optional second line under the asset — carries a cross-surface link. */
  note?: readonly Fragment[]
}

/**
 * The test set's own size. It collides numerically with the group's 214
 * library assets (`GROUP_STATS.library.assets`) and is NOT that figure: this
 * is one pack's regression suite, counted on its own.
 */
const REGRESSION_CASES = 214
/** Added in v4, from Van Dijk's pricing failures (deck; also in testUat.ts). */
const RETURNED_CASES = 22

export const CONTENTS: readonly TemplateAsset[] = [
  {
    key: 'blueprint',
    asset: `retail blueprint ${PACK.version}`,
    type: 'Blueprint',
    phase: 'design',
    phaseLabel: phaseName('design'),
    note: [
      { text: 'Sections, standard-vs-custom rules and the decisions raised to the client — governed in ' },
      { text: 'blueprint configuration', to: HREFS.blueprints },
      { text: '.' },
    ],
  },
  {
    key: 'config',
    asset: 'pricing config set',
    type: 'Config',
    phase: 'build',
    phaseLabel: phaseName('build'),
  },
  {
    key: 'mappings',
    asset: 'price-list mappings',
    type: 'Mapping',
    phase: 'migrate',
    phaseLabel: phaseName('migrate'),
  },
  {
    key: 'tests',
    asset: `${REGRESSION_CASES} regression cases`,
    type: 'Test set',
    phase: 'test',
    phaseLabel: phaseName('test'),
    note: [
      { text: `${RETURNED_CASES} of these came back from ` },
      { text: projectById(SOURCE_PROJECT_ID).client, to: HREFS.vanDijkTest },
      { text: ' — they run on every project that installs the pack.' },
    ],
  },
  {
    key: 'training',
    asset: 'store-staff training deck',
    type: 'Training',
    phase: 'go-live',
    phaseLabel: phaseName('go-live'),
  },
]

export const CONTENTS_TABLE = {
  label: 'Contents',
  heading: `What installing ${PACK.name.toLowerCase()} ${PACK.version} puts into a project`,
  columns: { asset: 'Asset', type: 'Type', phase: 'Phase' },
  caption:
    `${CONTENTS.length} assets, one for each phase from ${phaseName('design')} to ${phaseName('go-live')}. ` +
    `${phaseName('discover')} and ${phaseName('maintain')} are not templated: scope comes from the client, and ` +
    'AMS is written from the project that ran.',
} as const

/* ── 2 · Used by (project · office · outcome) ───────────────────────────── */

export type Outcome = 'In use' | 'Partial' | 'Delivered'

export interface TemplateUsage {
  key: string
  client: string
  meta: string
  href: string
  office: string
  outcome: Outcome
  outcomeNote: string
}

const usage = (
  projectId: string,
  installedVersion: string,
  outcome: Outcome,
  outcomeNote: string,
  href: string
): TemplateUsage => {
  const project = projectById(projectId)
  return {
    key: projectId,
    client: project.client,
    meta: `Odoo ${project.odooVersion} · ${project.vertical} · installed ${installedVersion}`,
    href,
    office: officeLabel(project.office),
    outcome,
    outcomeNote,
  }
}

export const USED_BY: readonly TemplateUsage[] = [
  usage(
    SOURCE_PROJECT_ID,
    PACK.version,
    'In use',
    `In ${phaseName('build').toLowerCase()} — the pack’s regressions run nightly against the build`,
    HREFS.vanDijk
  ),
  usage(
    PROMO_SOURCE_ID,
    PACK.previousVersion,
    'Delivered',
    `Live and in AMS — its promotion rule is what became ${PACK.version}`,
    HREFS.projects
  ),
  usage(
    ROUNDING_SOURCE_ID,
    PACK.version,
    'In use',
    `In ${phaseName('migrate').toLowerCase()} — the price-list mappings are carrying the legacy load`,
    HREFS.projects
  ),
]

export const USED_BY_TABLE = {
  label: 'Used by',
  heading: 'Where the pack is running now',
  columns: { project: 'Project', office: 'Office', outcome: 'Outcome' },
  caption: [
    { text: `${USED_BY.length} of the ${PACK.reuseCount} projects that have installed this pack are in this ` },
    { text: 'portfolio', to: HREFS.projects },
    { text: '; the rest have closed and are counted in the reuse score.' },
  ] as readonly Fragment[],
} as const

/* ── 3 · Improvements returned (version · rule/change · source project) ─── */

export interface TemplateChange {
  key: string
  version: string
  change: string
  /** What the previous version did — the compare panel reads this. */
  before: string
  /** Which asset in CONTENTS the change landed in. */
  landsAssetKey: string
  sourceClient: string
  sourceHref: string
  evidence: string
}

export const IMPROVEMENTS: readonly TemplateChange[] = [
  {
    key: 'regression-cases',
    version: PACK.version,
    change: `${RETURNED_CASES} regression cases for overlapping promotions and store-level price lists`,
    before: `${REGRESSION_CASES - RETURNED_CASES} cases, none of them covering two promotions on one line`,
    landsAssetKey: 'tests',
    sourceClient: projectById(SOURCE_PROJECT_ID).client,
    sourceHref: HREFS.vanDijkTest,
    evidence: 'Seven pricing failures in the nightly regressions',
  },
  {
    key: 'promo-priority',
    version: PACK.version,
    change: 'A promotion rule must declare a priority before it can overlap another one',
    before: 'Overlapping promotions resolved by whichever rule was created last',
    landsAssetKey: 'blueprint',
    sourceClient: projectById(PROMO_SOURCE_ID).client,
    sourceHref: HREFS.projects,
    evidence: 'Two years of AMS tickets on the same question',
  },
  {
    key: 'rounding',
    version: PACK.version,
    change: 'Price rounding is applied after tax, per price list, not once at the company level',
    before: 'One company-level rounding rule, which drifted a cent on French tax rates',
    landsAssetKey: 'config',
    sourceClient: projectById(ROUNDING_SOURCE_ID).client,
    sourceHref: HREFS.projects,
    evidence: 'A cent of drift on 40 000 migrated price lines',
  },
]

export const IMPROVEMENTS_PANEL = {
  label: 'Improvements returned',
  heading: `What the projects sent back into ${PACK.version}`,
  columns: { version: 'Version', change: 'Rule / change', source: 'From' },
  quote: 'A defect found in one country becomes a regression test everywhere.',
  landsLabel: 'lands in',
  closing: [
    { text: 'Every change here was accepted by the pack maintainer, ' },
    { text: MAINTAINER.name, to: MAINTAINER_LINK.href },
    {
      text:
        `. Nothing is harvested silently: a project proposes, the maintainer merges, and the ` +
        `${PACK.version} that the next rollout installs is the merged one.`,
    },
  ] as readonly Fragment[],
} as const

/* ── 4 · Compare v3 → v4 (inline, real content — not a stub) ────────────── */

const CHANGED_ASSET_KEYS = new Set(IMPROVEMENTS.map((improvement) => improvement.landsAssetKey))

const assetName = (key: string): string =>
  CONTENTS.find((asset) => asset.key === key)?.asset ?? key

const UNCHANGED_ASSETS = CONTENTS.filter((asset) => !CHANGED_ASSET_KEYS.has(asset.key))

export interface CompareRow {
  key: string
  asset: string
  before: string
  after: string
  source: string
}

export const COMPARE = {
  label: `Compare ${PACK.previousVersion} → ${PACK.version}`,
  openLabel: `Compare ${PACK.previousVersion} → ${PACK.version}`,
  closeLabel: `Hide the ${PACK.previousVersion} → ${PACK.version} comparison`,
  consequence: 'Opens the diff on this page — nothing is installed or changed.',
  heading: `${IMPROVEMENTS.length} changes between ${PACK.previousVersion} and ${PACK.version}`,
  columns: {
    asset: 'Asset',
    before: `${PACK.previousVersion} did`,
    after: `${PACK.version} does`,
    source: 'Came from',
  },
  rows: IMPROVEMENTS.map(
    (improvement): CompareRow => ({
      key: improvement.key,
      asset: assetName(improvement.landsAssetKey),
      before: improvement.before,
      after: improvement.change,
      source: improvement.sourceClient,
    })
  ),
  unchanged:
    `Unchanged in ${PACK.version}: ${UNCHANGED_ASSETS.map((asset) => asset.asset).join(' and ')}. ` +
    `Nothing was retired — a project on ${PACK.previousVersion} upgrades by taking the ${IMPROVEMENTS.length} ` +
    'changes above, not by reinstalling the pack.',
} as const

/* ── 5 · Actions (each one honest about what it does) ───────────────────── */

export type TemplateAction =
  | {
      kind: 'link'
      key: string
      label: string
      consequence: string
      to: string
      emphasis: 'decision' | 'quiet'
    }
  | { kind: 'toggle'; key: string; label: string; consequence: string }

export const ACTIONS: readonly TemplateAction[] = [
  {
    kind: 'link',
    key: 'open-in-project',
    label: 'Open in a project',
    consequence: `Pre-fills the blueprint of the project you choose — here, ${projectById(SOURCE_PROJECT_ID).client}. A person still reviews every section before the Design gate.`,
    to: HREFS.vanDijkBlueprint,
    emphasis: 'decision',
  },
  {
    kind: 'toggle',
    key: COMPARE.heading,
    label: COMPARE.openLabel,
    consequence: COMPARE.consequence,
  },
  {
    kind: 'link',
    key: 'propose-a-change',
    label: 'Propose a change',
    consequence:
      'Configuration changes to this pack are governed on the blueprint, where the rules and the required decisions live.',
    to: HREFS.blueprints,
    emphasis: 'quiet',
  },
  {
    kind: 'link',
    key: 'assign-to-cohort',
    label: 'Assign to a training cohort',
    consequence:
      'The Competence Center teaches from this pack, anonymised — the cohort schedule is where that is booked.',
    to: HREFS.training,
    emphasis: 'quiet',
  },
]

export const ACTIONS_BLOCK = {
  label: 'Actions',
  heading: 'What you can do with this pack',
} as const

/* ── 6 · What this page withholds ───────────────────────────────────────── */

export const DENSITY_NOTE: readonly Fragment[] = [
  {
    text:
      `Contents are listed at asset level — the files inside each asset are withheld, so the ` +
      `${REGRESSION_CASES} regression cases appear as one row rather than a list this prototype does not hold; ` +
      `the ${RETURNED_CASES} that came back are reachable on `,
  },
  { text: `${projectById(SOURCE_PROJECT_ID).client}’s test board`, to: HREFS.vanDijkTest },
  {
    text:
      `. Usage shows the ${USED_BY.length} projects seeded in this portfolio, not all ${PACK.reuseCount} reuses — ` +
      `the reuse score is computed at project close, when the pack is harvested. Only this pack is authored: ` +
      `every other template id opens it, and the full list stays on `,
  },
  { text: 'the library', to: HREFS.templates },
  { text: '.' },
]
